import { RefObject, useCallback, useState } from "react"

import { useTheme } from "@mui/material"
import { flushSync } from "react-dom"

const isCaptured = (node: HTMLElement) =>
  !(node instanceof HTMLElement && node.hasAttribute("data-export-ignore"))

export const useExportNodeAsPng = (
  ref: RefObject<HTMLElement>,
  filename: string,
) => {
  const theme = useTheme()
  const [isExporting, setIsExporting] = useState(false)

  const exportPng = useCallback(async () => {
    if (!ref.current) return
    // Committed before the capture, so export-only content (e.g. a caption) is in the image.
    flushSync(() => setIsExporting(true))
    try {
      const { toPng } = await import("html-to-image")
      const options = {
        backgroundColor: theme.palette.common.white,
        pixelRatio: 2,
        filter: isCaptured,
      }
      // Warm-up pass: Safari (and Chromium with web fonts) can draw the first one blank.
      await toPng(ref.current, options)
      const dataUrl = await toPng(ref.current, options)
      const link = document.createElement("a")
      link.download = `${filename}.png`
      link.href = dataUrl
      link.click()
    } finally {
      setIsExporting(false)
    }
  }, [ref, filename, theme])

  return { exportPng, isExporting }
}
